import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Animates vertical expand/collapse via max-height using ResizeObserver.
 * - open: controls open/close
 * - durationMs, ease: animation speed/curve
 * - observeChanges: also animates later content changes (e.g., additional "more steps")
 */
export default function ExpandCollapse({
  open,
  children,
  className = "",
  durationMs = 700,
  ease = "cubic-bezier(.2,.8,.2,1)",
  observeChanges = true,
}) {
  const ref = useRef(null);
  const [maxH, setMaxH] = useState(0);

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  // measure height
  const measure = () => {
    const el = ref.current;
    if (!el) return 0;
    // content is the first child
    const content = el.firstElementChild;
    const h = content ? content.scrollHeight : 0;
    return h;
  };

  // on mount set initial max-height
  useLayoutEffect(() => {
    if (!ref.current) return;
    setMaxH(open ? measure() : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // animate open/close
  useEffect(() => {
    if (!ref.current) return;

    if (prefersReduced) {
      // without animation
      ref.current.style.transition = "none";
      setMaxH(open ? measure() : 0);
      return;
    }

    // enable transition
    ref.current.style.transitionProperty = "max-height, opacity, transform";
    ref.current.style.transitionDuration = `${durationMs}ms`;
    ref.current.style.transitionTimingFunction = ease;

    if (open) {
      // from current height to target height
      const to = measure();
      // if closed, briefly 0 -> to
      setMaxH((curr) => (curr === 0 ? 0 : curr));
      // next frame: to target height
      requestAnimationFrame(() => setMaxH(to));
    } else {
      // from current height to 0
      const from = measure();
      setMaxH(from);
      requestAnimationFrame(() => setMaxH(0));
    }
  }, [open, durationMs, ease, prefersReduced]);

  // smoothly animate later content changes (e.g., additional "more steps")
  useEffect(() => {
    if (!observeChanges || !ref.current) return;
    const ro = new ResizeObserver(() => {
      if (!open) return; // only when open
      const to = measure();
      setMaxH(to);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [open, observeChanges]);

  return (
    <div
      ref={ref}
      className={`overflow-hidden will-change-[max-height,opacity,transform] ${className}`}
      style={{
        maxHeight: maxH,
        opacity: open ? 1 : 0.98,
        transform: open ? "translateY(0)" : "translateY(-2px)",
      }}
      aria-hidden={!open}
    >
      <div>{children}</div>
    </div>
  );
}
